import { SupportChatApiError } from './types';
import { supportChatFetch } from './http';
async function parseJson(res) {
    const text = await res.text();
    if (!text)
        return undefined;
    try {
        return JSON.parse(text);
    }
    catch {
        throw new SupportChatApiError('Invalid JSON response', res.status, text);
    }
}
async function expectOk(res) {
    if (res.ok)
        return;
    let body;
    try {
        body = await res.json();
    }
    catch {
        body = await res.text();
    }
    throw new SupportChatApiError(`Support chat HTTP ${res.status}`, res.status, body);
}
function asConversationArray(body) {
    if (Array.isArray(body))
        return body;
    if (body && typeof body === 'object') {
        const o = body;
        if (Array.isArray(o.conversations))
            return o.conversations;
        if (Array.isArray(o.data))
            return o.data;
    }
    return [];
}
function asMessagesPage(body) {
    if (body && typeof body === 'object' && 'messages' in body) {
        return body;
    }
    throw new SupportChatApiError('Unexpected messages response shape', 500, body);
}
/** POST /messages: `{ messages: [customer, ai?] }` — AI reply omitted when a human handles the thread. */
function asPostedMessages(body) {
    if (body && typeof body === 'object' && 'messages' in body) {
        const o = body;
        if (Array.isArray(o.messages))
            return o.messages;
    }
    throw new SupportChatApiError('Unexpected send message response shape', 500, body);
}
export async function createSupportConversation(customerUserId) {
    const res = await supportChatFetch('/conversations', {
        method: 'POST',
        json: { customer_user_id: customerUserId }
    });
    await expectOk(res);
    return parseJson(res);
}
export async function listSupportConversations() {
    const res = await supportChatFetch('/conversations', { method: 'GET' });
    await expectOk(res);
    const body = await parseJson(res);
    return asConversationArray(body);
}
export async function getSupportConversation(id) {
    const res = await supportChatFetch(`/conversations/${encodeURIComponent(id)}`, { method: 'GET' });
    await expectOk(res);
    return parseJson(res);
}
/**
 * Returns messages from the send response: index 0 is the customer message; index 1 when present
 * is a synchronous AI reply. With human handling there is no second element; further messages may
 * still arrive via WebSocket.
 */
export async function sendSupportMessage(conversationId, content) {
    const res = await supportChatFetch(`/conversations/${encodeURIComponent(conversationId)}/messages`, {
        method: 'POST',
        json: { content }
    });
    await expectOk(res);
    const body = await parseJson(res);
    const messages = asPostedMessages(body);
    if (!messages.length) {
        throw new SupportChatApiError('Send message returned no messages', res.status, body);
    }
    return messages;
}
export async function getSupportConversationMessages(conversationId, limit = 50, offset = 0) {
    const q = new URLSearchParams({
        limit: String(limit),
        offset: String(offset)
    });
    const res = await supportChatFetch(`/conversations/${encodeURIComponent(conversationId)}/messages?${q.toString()}`, { method: 'GET' });
    await expectOk(res);
    const body = await parseJson(res);
    return asMessagesPage(body);
}
export async function requestSupportHandoff(conversationId, reason) {
    const res = await supportChatFetch(`/conversations/${encodeURIComponent(conversationId)}/handoff`, {
        method: 'POST',
        json: { reason }
    });
    await expectOk(res);
}
